
import { Drawer, Slider, Typography, Input, InputAdornment, Button, Box } from '@mui/material'
import Grid from '@mui/material/Grid';
import { useState, useEffect } from "react";
import HeightIcon from '@mui/icons-material/Height';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import Divider from '@mui/material/Divider';
import './i18n/config';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';

function InputSlider(props: any) {
    const { id, min = 0, max = 100, value = 30, step = 10, description = '', label = '', unit = '', icon = null, onValueChange = (value: number) => { }, ...rest } = props;

    const valueChanged = (value: number): void => {
        onValueChange(value);
    }

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        valueChanged(newValue as number);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        valueChanged(event.target.value === '' ? 0 : Number(event.target.value));
    };

    const handleBlur = () => {
        if (value < min) {
            valueChanged(min);
        } else if (value > max) {
            valueChanged(max);
        }
    };

    const labelId = `${id}-label`
    const sliderId = `${id}-slider`
    const inputId = `${id}-input`

    return (
        <>
            <Box textAlign='center'>
                <Tooltip id={labelId} title={description} enterTouchDelay={0}>
                    <Button>{label}</Button>
                </Tooltip>
            </Box>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                <Grid item>
                    {icon}
                </Grid>
                <Grid item xs>
                    <Slider
                        value={typeof value === 'number' ? value : 0}
                        min={min}
                        max={max}
                        step={step}
                        onChange={handleSliderChange}
                        id={sliderId}
                        aria-labelledby={labelId}
                    />
                </Grid>
                <Grid item>
                    <Input
                        id={inputId}
                        value={value}
                        size="small"
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        endAdornment={<InputAdornment position="end">{unit}</InputAdornment>}
                        inputProps={{
                            step: step,
                            min: min,
                            max: max,
                            type: 'number',
                            'aria-labelledby': 'input-slider',
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
}

export function CanopyCutawayLocationButton(props: any) {
    const { t } = useTranslation();
    const { onClick = () => { }, ...rest } = props;
    return (
        <Button variant="contained" onClick={onClick}>{t("Set canopy cutaway location")}</Button>
    )
}

export function CutawayInputs(props: any) {
    function valuetext(value: number) {
        return `${value}m`;
    }
    const { t } = useTranslation();

    const { onSetCutawayLocation = () => { }, releaseLocation = { lat: null, lng: null }, cutawayParameters = {}, onParametersChanged = (params: any) => { }, ...rest } = props;
    const releaseLocationValid = releaseLocation.lat !== null && releaseLocation.lng !== null;
    if (releaseLocationValid) {
        var releaseLink = <a target='_blank' href={`https://maps.google.com/?q=${releaseLocation.lat},${releaseLocation.lng}`}>{`${releaseLocation.lat.toFixed(5)}, ${releaseLocation.lng.toFixed(5)}`}</a>;
        var releaseInformation = t("Release location") + ": ";
    } else {
        var releaseLink = <></>
        var releaseInformation = t('Release location not set');
    }


    const [cutawayHeight, setCutawayHeight] = useState(cutawayParameters.cutawayHeight || 800);
    const [cutawaySinkRate, setCutawaySinkRate] = useState(cutawayParameters.cutawaySinkRate || 3.6);
    const [cutawayDate, setCutawayDate] = useState<Dayjs | null>(
        cutawayParameters.cutawayDate ? dayjs(cutawayParameters.cutawayDate) : dayjs(new Date())
    );
    
    // Calculate current time value for the slider
    const currentTimeValue = cutawayHeight / cutawaySinkRate;

    // Update state when parameters change (from other users)
    useEffect(() => {
        if (cutawayParameters.cutawayHeight !== undefined) {
            setCutawayHeight(cutawayParameters.cutawayHeight);
        }
        if (cutawayParameters.cutawaySinkRate !== undefined) {
            setCutawaySinkRate(cutawayParameters.cutawaySinkRate);
        }
        if (cutawayParameters.cutawayDate) {
            setCutawayDate(dayjs(cutawayParameters.cutawayDate));
        }
    }, [cutawayParameters]);

    const onCutawayHeightChanged = (value: number) => {
        onParametersChanged({ cutawaySinkRate: cutawaySinkRate, cutawayDate: cutawayDate?.toISOString(), cutawayHeight: value });
        setCutawayHeight(value);
    }

    const onCutawaySinkRateChanged = (value: number) => {
        onParametersChanged({ cutawayHeight: cutawayHeight, cutawayDate: cutawayDate?.toISOString(), cutawaySinkRate: value });
        setCutawaySinkRate(value);
    }

    const onCutawayDateChanged = (value: Dayjs | null) => {
        onParametersChanged({ cutawaySinkRate: cutawaySinkRate, cutawayHeight: cutawayHeight, cutawayDate: value?.toISOString() });
        setCutawayDate(value);
    }

    const cutawayTimeChanged = (value: number) => {
        const newSinkRate = cutawayHeight / value;
        onParametersChanged({ 
            cutawaySinkRate: newSinkRate, 
            cutawayHeight: cutawayHeight, 
            cutawayDate: cutawayDate?.toISOString() 
        });
        setCutawaySinkRate(newSinkRate);
    }

    // Send initial state to parent component
    useEffect(() => {
        onParametersChanged({ cutawaySinkRate: cutawaySinkRate, cutawayDate: cutawayDate, cutawayHeight: cutawayHeight });
    }, []);

    return (
        <>
            <Drawer {...rest} PaperProps={{
                sx: { maxWidth: "300px" },
            }}>
                <div style={{ marginRight: 10, marginLeft: 10 }} id='cutaway-parameters-div'>
                    <h2>{t("Cutaway finding parameters")}</h2>

                    <Divider />
                    <div id='cutaway-location-div'>
                        <Typography>{releaseInformation} {releaseLink}</Typography>
                        <CanopyCutawayLocationButton onClick={onSetCutawayLocation}></CanopyCutawayLocationButton>
                    </div>
                    <br /><br />

                    <Divider />
                    <div id='cutaway-height-div'>
                        <InputSlider onValueChange={onCutawayHeightChanged} value={cutawayHeight} id="cutaway-height" icon={<HeightIcon />} description={t("cutaway_height_description")} label={t("cutawayaltitude-label")} max={4000} unit={'m'}></InputSlider>
                    </div>
                    <br />

                    <Divider />
                    <div id='cutaway-sink-rate-div'>
                        <InputSlider onValueChange={onCutawaySinkRateChanged} value={cutawaySinkRate} id="cutaway-sink-rate" icon={<SpeedIcon />} description={t("cutaway_sink_rate_description")} label={t("cutaway-sink-rate-label")} step={0.1} max={15} unit={'m/s'}></InputSlider>
                    </div>
                    <br />

                    <Divider />
                    <div id='cutaway-time-div'>
                        <InputSlider onValueChange={cutawayTimeChanged} value={currentTimeValue} id="cutaway-sink-time" icon={<TimerOutlinedIcon />} description={t("cutaway_time_description")} label={t("cutaway-sink-time-label")} max={600} unit={'s'}></InputSlider>
                    </div>
                    <br />

                    <Divider />
                    <div id='cutaway-date-div'>
                        {false && t("cutaway_date_description")}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                ampm={false}
                                label={t("cutaway_date_label")}
                                value={cutawayDate}
                                onChange={onCutawayDateChanged}
                            />
                        </LocalizationProvider>
                    </div>
                </div>
            </Drawer>
        </>
    )
}